import React from 'react';
import { Layout } from '../components/Layout';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { CheckCircle, Search, MessageSquare, Calendar, Shield, CreditCard } from 'lucide-react';

const HowItWorksPage = () => {
  usePageMetadata();

  const renterSteps = [
    {
      icon: Search,
      title: "Browse Listings",
      description: "Search our curated selection of mid-term rentals in Los Angeles County. Filter by location, price, amenities, and availability."
    },
    {
      icon: MessageSquare,
      title: "Connect with Owners",
      description: "Message property owners directly through our platform. Ask questions, request tours, and discuss your needs."
    },
    {
      icon: Calendar,
      title: "Book Your Stay",
      description: "Once you find the perfect place, submit a booking request. Owners can approve instantly or review your application."
    },
    {
      icon: CreditCard,
      title: "Pay Securely",
      description: "Complete your payment through our secure platform. All transactions are protected and funds are released safely."
    }
  ];

  const ownerSteps = [
    {
      icon: Shield,
      title: "Create Your Account",
      description: "Sign up as a property owner and choose a subscription plan that fits your needs."
    },
    {
      icon: Search,
      title: "List Your Property",
      description: "Add photos, set your price, define house rules, and describe your space to attract the right renters."
    },
    {
      icon: MessageSquare,
      title: "Review Requests",
      description: "Receive booking requests and messages from interested renters. Chat directly to qualify potential tenants."
    },
    {
      icon: CreditCard,
      title: "Get Paid",
      description: "Payments are processed securely and deposited directly to your account. No per-booking fees."
    }
  ];

  return (
    <Layout>
      <div className="bg-gradient-to-b from-slate-50 to-white min-h-screen">
        {/* Hero */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              How Stay Pair Homes Works
            </h1>
            <p className="text-xl text-slate-600">
              Simple, direct connections between property owners and renters for flexible mid-term housing.
            </p>
          </div>
        </section>

        {/* For Renters */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">For Renters</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {renterSteps.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-trust-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <step.icon className="w-8 h-8 text-trust-blue" />
                  </div>
                  <div className="w-8 h-8 bg-trust-blue text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                    {index + 1}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* For Owners */}
        <section className="py-16 px-4 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">For Property Owners</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {ownerSteps.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-coral/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <step.icon className="w-8 h-8 text-coral" />
                  </div>
                  <div className="w-8 h-8 bg-coral text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                    {index + 1}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Why Choose Stay Pair Homes?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { title: "Direct Communication", desc: "No middlemen. Chat directly with owners or renters." },
                { title: "Transparent Pricing", desc: "No hidden fees. Owners pay a simple monthly subscription." },
                { title: "Verified Listings", desc: "All properties are reviewed for quality and accuracy." },
                { title: "Secure Payments", desc: "Protected transactions through our trusted platform." },
                { title: "Flexible Terms", desc: "Find stays from 30 days to 12 months or longer." },
                { title: "Local Focus", desc: "Specialized in Los Angeles County housing market." }
              ].map((benefit, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <div>
                    <h3 className="font-semibold text-slate-900">{benefit.title}</h3>
                    <p className="text-slate-600 text-sm">{benefit.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default HowItWorksPage;
